import { supabase } from '@/integrations/supabase/client';
import {
	QueryGroup,
	QueryCondition,
	isQueryGroup,
	isQueryCondition,
	getFieldDefinition,
	CANDIDATE_FIELDS
} from '@/types/queryBuilder';
import { Candidate, CEFR_LEVELS } from '@/types/candidate';

// Map string keys to CEFR level labels for preview
const CEFR_LEVEL_LABELS: Record<string, string> = Object.fromEntries(
	Object.entries(CEFR_LEVELS).map(([k, v]) => [String(k), v])
);

// Resolve a language condition to a set of matching candidate IDs
async function resolveLanguageCondition(condition: QueryCondition): Promise<string[]> {
	const { operator, value, secondValue } = condition;
	const languageName = (value as string || '').trim();

	if (!languageName)
		return [];

	// @ts-ignore
	let query = supabase
		.from('candidate_languages')
		.select('candidate_id')
		.ilike('language', languageName);

	if (operator !== 'any_level' && secondValue) {
		const level = parseInt(secondValue as string, 10);
		if (!isNaN(level)) {
			if (operator === 'at_least')
				// @ts-ignore
				query = query.gte('cefr_level', level);
			else if (operator === 'at_most')
				// @ts-ignore
				query = query.lte('cefr_level', level);
			else if (operator === 'exactly')
				// @ts-ignore
				query = query.eq('cefr_level', level);
		}
	}

	// @ts-ignore
	const { data } = await query;
	return (data || []).map((r: any) => r.candidate_id);
}

// Collect all language conditions from a group tree
function collectLanguageConditions(group: QueryGroup): QueryCondition[] {
	const result: QueryCondition[] = [];
	for (const item of group.conditions) {
		if (isQueryCondition(item) && item.field === 'language')
			result.push(item);
		else if (isQueryGroup(item))
			result.push(...collectLanguageConditions(item));
	}
	return result;
}

// Get current organization UUID
async function getCurrentOrganization(): Promise<string> {
	const { data: { user } } = await supabase.auth.getUser();
	if (!user) throw new Error('User not authenticated');

	const { data: userOrgs } = await supabase
		.from('user_organization')
		.select('organization_uuid')
		.eq('user_uuid', user.id)
		.single();

	if (!userOrgs) throw new Error('User not associated with any organization');
	return userOrgs.organization_uuid;
}

// Filter type for database vs client-side filtering
interface FilterResult {
	column: string;
	filter: string;
	value: any;
	clientSide?: boolean;
	clientSideType?: 'array_any' | 'array_all';
}

// Build a single condition into filter format
function buildConditionFilter(condition: QueryCondition, resolvedLanguageIds?: Map<string, string[]>): FilterResult | null {
	const { field, operator, value, secondValue } = condition;
	const fieldDef = getFieldDefinition(field);
	const isArrayField = fieldDef?.type === 'array';

	// Language conditions are resolved to candidate ID sets
	if (field === 'language') {
		const ids = resolvedLanguageIds?.get(condition.id);
		if (ids === undefined)
			return null;
		return { column: 'id', filter: 'in', value: ids.length > 0 ? ids : ['__no_match__'] };
	}

	// Skip if value is required but empty
	if (['is_empty', 'is_not_empty'].indexOf(operator) === -1 && (value === '' || value === null))
		return null;

	// Skip if array value is empty
	if (Array.isArray(value) && value.length === 0)
		return null;

	switch (operator) {
		case 'equals':
			return { column: field, filter: 'ilike', value };
		case 'not_equals':
			return { column: field, filter: 'not.ilike', value };
		case 'contains':
			return { column: field, filter: 'ilike', value: `%${value}%` };
		case 'not_contains':
			return { column: field, filter: 'not.ilike', value: `%${value}%` };
		case 'starts_with':
			return { column: field, filter: 'ilike', value: `${value}%` };
		case 'ends_with':
			return { column: field, filter: 'ilike', value: `%${value}` };
		case 'is_empty':
			return { column: field, filter: 'is', value: null };
		case 'is_not_empty':
			return { column: field, filter: 'not.is', value: null };
		case 'contains_any':
			// For array fields, use client-side filtering for case-insensitivity
			if (isArrayField) {
				const values = Array.isArray(value) ? value : [value];
				return { column: field, filter: 'client', value: values, clientSide: true, clientSideType: 'array_any' };
			}
			return { column: field, filter: 'ov', value: Array.isArray(value) ? value : [value] };
		case 'contains_all':
			// For array fields, use client-side filtering for case-insensitivity
			if (isArrayField) {
				const values = Array.isArray(value) ? value : [value];
				return { column: field, filter: 'client', value: values, clientSide: true, clientSideType: 'array_all' };
			}
			return { column: field, filter: 'cs', value: Array.isArray(value) ? value : [value] };
		case 'in':
			return { column: field, filter: 'in', value: Array.isArray(value) ? value : [value] };
		case 'not_in':
			return { column: field, filter: 'not.in', value: Array.isArray(value) ? value : [value] };
		case 'before':
			return { column: field, filter: 'lt', value };
		case 'after':
			return { column: field, filter: 'gt', value };
		case 'between':
			return { column: field, filter: 'gte', value };
		default:
			return null;
	}
}

// Check if a group has any client-side filters
function hasClientSideFilters(group: QueryGroup, resolvedLanguageIds?: Map<string, string[]>): boolean {
	for (const item of group.conditions) {
		if (isQueryCondition(item)) {
			const filter = buildConditionFilter(item, resolvedLanguageIds);
			if (filter?.clientSide)
				return true;
		} else if (isQueryGroup(item)) {
			if (hasClientSideFilters(item, resolvedLanguageIds))
				return true;
		}
	}
	return false;
}

// Collect all client-side filters from a query group
function collectClientSideFilters(group: QueryGroup, resolvedLanguageIds?: Map<string, string[]>): { filter: FilterResult; groupType: 'AND' | 'OR' }[] {
	const filters: { filter: FilterResult; groupType: 'AND' | 'OR' }[] = [];

	for (const item of group.conditions) {
		if (isQueryCondition(item)) {
			const filter = buildConditionFilter(item, resolvedLanguageIds);
			if (filter?.clientSide)
				filters.push({ filter, groupType: group.type });
		} else if (isQueryGroup(item)) {
			filters.push(...collectClientSideFilters(item, resolvedLanguageIds));
		}
	}

	return filters;
}

// Evaluate a single condition client-side
function evaluateConditionClientSide(candidate: Candidate, condition: QueryCondition, resolvedLanguageIds?: Map<string, string[]>): boolean {
	const filter = buildConditionFilter(condition, resolvedLanguageIds);
	if (!filter)
		return true;

	const fieldValue = (candidate as any)[filter.column];

	// Handle client-side array filters
	if (filter.clientSideType === 'array_any') {
		if (!Array.isArray(fieldValue))
			return false;

		const searchValues = filter.value as string[];
		const lowerFieldValues = fieldValue.map((v: string) => v.toLowerCase());

		return searchValues.some(searchVal =>
			lowerFieldValues.some((fieldVal: string) =>
				fieldVal.includes(searchVal.toLowerCase())
			)
		);
	}

	if (filter.clientSideType === 'array_all') {
		if (!Array.isArray(fieldValue))
			return false;

		const searchValues = filter.value as string[];
		const lowerFieldValues = fieldValue.map((v: string) => v.toLowerCase());

		return searchValues.every(searchVal =>
			lowerFieldValues.some((fieldVal: string) =>
				fieldVal.includes(searchVal.toLowerCase())
			)
		);
	}

	// Handle database-style filters client-side
	const value = filter.value;
	const stringValue = typeof fieldValue === 'string' ? fieldValue : String(fieldValue || '');

	switch (filter.filter) {
		case 'ilike': {
			// Handle SQL LIKE wildcards: % means any characters
			const pattern = String(value).toLowerCase();
			const lowerValue = stringValue.toLowerCase();

			if (pattern.startsWith('%') && pattern.endsWith('%')) {
				// Contains
				const searchStr = pattern.slice(1, -1);
				return lowerValue.includes(searchStr);
			} else if (pattern.startsWith('%')) {
				// Ends with
				const searchStr = pattern.slice(1);
				return lowerValue.endsWith(searchStr);
			} else if (pattern.endsWith('%')) {
				// Starts with
				const searchStr = pattern.slice(0, -1);
				return lowerValue.startsWith(searchStr);
			} else {
				// Exact match (case-insensitive)
				return lowerValue === pattern;
			}
		}
		case 'not.ilike': {
			// Handle SQL LIKE wildcards for NOT ILIKE
			const pattern = String(value).toLowerCase();
			const lowerValue = stringValue.toLowerCase();

			if (pattern.startsWith('%') && pattern.endsWith('%')) {
				const searchStr = pattern.slice(1, -1);
				return !lowerValue.includes(searchStr);
			} else if (pattern.startsWith('%')) {
				const searchStr = pattern.slice(1);
				return !lowerValue.endsWith(searchStr);
			} else if (pattern.endsWith('%')) {
				const searchStr = pattern.slice(0, -1);
				return !lowerValue.startsWith(searchStr);
			} else {
				return lowerValue !== pattern;
			}
		}
		case 'is':
			return fieldValue === null;
		case 'not.is':
			return fieldValue !== null;
		case 'eq':
			return fieldValue === value;
		case 'neq':
			return fieldValue !== value;
		case 'in':
			return Array.isArray(value) && value.includes(fieldValue);
		case 'not.in':
			return Array.isArray(value) && !value.includes(fieldValue);
		case 'lt':
			return fieldValue < value;
		case 'gt':
			return fieldValue > value;
		case 'lte':
			return fieldValue <= value;
		case 'gte':
			return fieldValue >= value;
		default:
			return true;
	}
}

// Evaluate a query group client-side
function evaluateGroupClientSide(candidate: Candidate, group: QueryGroup, resolvedLanguageIds?: Map<string, string[]>): boolean {
	if (group.conditions.length === 0)
		return true;

	const results = group.conditions.map(item => {
		if (isQueryCondition(item))
			return evaluateConditionClientSide(candidate, item, resolvedLanguageIds);
		else if (isQueryGroup(item))
			return evaluateGroupClientSide(candidate, item, resolvedLanguageIds);
		return true;
	});

	// Apply group logic (AND/OR)
	if (group.type === 'OR')
		return results.some(r => r);
	else
		return results.every(r => r);
}

// Apply client-side filters to results (legacy - for AND-only scenarios)
function applyClientSideFilters(
	candidates: Candidate[],
	filters: { filter: FilterResult; groupType: 'AND' | 'OR' }[]
): Candidate[] {
	if (filters.length === 0)
		return candidates;

	return candidates.filter(candidate => {
		// For now, treat all filters as AND (most common case)
		return filters.every(({ filter }) => {
			const fieldValue = (candidate as any)[filter.column];

			if (filter.clientSideType === 'array_any') {
				// Check if any of the search values match any item in the array (case-insensitive)
				if (!Array.isArray(fieldValue))
					return false;

				const searchValues = filter.value as string[];
				const lowerFieldValues = fieldValue.map((v: string) => v.toLowerCase());

				return searchValues.some(searchVal =>
					lowerFieldValues.some((fieldVal: string) =>
						fieldVal.includes(searchVal.toLowerCase())
					)
				);
			}

			if (filter.clientSideType === 'array_all') {
				// Check if all search values match items in the array (case-insensitive)
				if (!Array.isArray(fieldValue))
					return false;

				const searchValues = filter.value as string[];
				const lowerFieldValues = fieldValue.map((v: string) => v.toLowerCase());

				return searchValues.every(searchVal =>
					lowerFieldValues.some((fieldVal: string) =>
						fieldVal.includes(searchVal.toLowerCase())
					)
				);
			}

			return true;
		});
	});
}

// Execute query and return candidates
export async function executeQuery(group: QueryGroup): Promise<Candidate[]> {
	const organizationUuid = await getCurrentOrganization();

	// Pre-resolve language conditions to candidate ID sets
	const languageConditions = collectLanguageConditions(group);
	const resolvedLanguageIds = new Map<string, string[]>();
	await Promise.all(
		languageConditions.map(async (cond) => {
			const ids = await resolveLanguageCondition(cond);
			resolvedLanguageIds.set(cond.id, ids);
		})
	);

	// Check if root group is OR with client-side filters
	const isOrWithClientSide = group.type === 'OR' && hasClientSideFilters(group, resolvedLanguageIds);

	// @ts-ignore - candidates table
	let query = supabase
		.from('candidates')
		.select('id, name, email, phone, function, skills, status, location, created_at, updated_at, manager, linkedin, custom_fields')
		.eq('organization_uuid', organizationUuid);

	// If root is OR with client-side filters, fetch all and filter client-side
	if (isOrWithClientSide) {
		const { data, error } = await query.order('name');
		if (error) throw error;

		const allCandidates = (data || []) as Candidate[];
		return allCandidates.filter(candidate => evaluateGroupClientSide(candidate, group, resolvedLanguageIds));
	}

	// Otherwise, use the normal flow
	const clientSideFilters = collectClientSideFilters(group, resolvedLanguageIds);

	// Apply database filters (excluding client-side ones)
	query = applyGroupFilters(query, group, true, resolvedLanguageIds);

	const { data, error } = await query.order('name');

	if (error) throw error;

	let results = (data || []) as Candidate[];

	// Apply client-side filters for case-insensitive array matching (AND logic only)
	if (clientSideFilters.length > 0)
		results = applyClientSideFilters(results, clientSideFilters);

	return results;
}

// Build filter string for a single condition (for OR/AND string building)
function buildFilterString(filter: FilterResult): string {
	const { column, filter: filterType, value } = filter;

	// Handle special cases for filters that need array formatting with parentheses
	if (filterType === 'in') {
		const arrayValue = Array.isArray(value) ? value : [value];
		return `${column}.in.(${arrayValue.map(v => typeof v === 'string' ? `"${v}"` : v).join(',')})`;
	}
	if (filterType === 'not.in') {
		const arrayValue = Array.isArray(value) ? value : [value];
		return `${column}.not.in.(${arrayValue.map(v => typeof v === 'string' ? `"${v}"` : v).join(',')})`;
	}

	// Handle other 'not.' filters
	if (filterType === 'not.ilike')
		return `${column}.not.ilike.${formatValue(value)}`;
	if (filterType === 'not.is')
		return `${column}.not.is.${formatValue(value)}`;

	// Standard filters
	return `${column}.${filterType}.${formatValue(value)}`;
}

// Build filter string for a group (used for nested groups in OR conditions)
function buildGroupFilterString(group: QueryGroup, skipClientSide: boolean = false, resolvedLanguageIds?: Map<string, string[]>): string {
	const parts: string[] = [];

	for (const item of group.conditions) {
		if (isQueryCondition(item)) {
			const filter = buildConditionFilter(item, resolvedLanguageIds);
			if (filter && !(skipClientSide && filter.clientSide))
				parts.push(buildFilterString(filter));
		} else if (isQueryGroup(item)) {
			const nestedStr = buildGroupFilterString(item, skipClientSide, resolvedLanguageIds);
			if (nestedStr)
				parts.push(`(${nestedStr})`);
		}
	}

	if (parts.length === 0)
		return '';

	// Join with AND or OR based on group type
	const separator = group.type === 'OR' ? ',' : ',and(';
	if (group.type === 'AND' && parts.length > 1)
		return `and(${parts.join(',')})`;

	return parts.join(separator);
}

// Apply group filters to query (skipClientSide = true to skip client-side filters)
function applyGroupFilters(query: any, group: QueryGroup, skipClientSide: boolean = false, resolvedLanguageIds?: Map<string, string[]>): any {
	if (group.conditions.length === 0)
		return query;

	// If this is an OR group, build all conditions as OR string
	if (group.type === 'OR') {
		const orParts: string[] = [];

		for (const item of group.conditions) {
			if (isQueryCondition(item)) {
				const filter = buildConditionFilter(item, resolvedLanguageIds);
				if (filter && !(skipClientSide && filter.clientSide))
					orParts.push(buildFilterString(filter));
			} else if (isQueryGroup(item)) {
				// Handle nested groups within OR
				const nestedGroupConditions = buildGroupFilterString(item, skipClientSide, resolvedLanguageIds);
				if (nestedGroupConditions)
					orParts.push(`(${nestedGroupConditions})`);
			}
		}

		if (orParts.length > 0)
			query = query.or(orParts.join(','));

		return query;
	}

	// For AND groups, apply each condition sequentially
	for (const item of group.conditions) {
		if (isQueryCondition(item)) {
			const filter = buildConditionFilter(item, resolvedLanguageIds);
			if (filter && !(skipClientSide && filter.clientSide))
				query = applyFilter(query, filter);
		} else if (isQueryGroup(item)) {
			query = applyGroupFilters(query, item, skipClientSide, resolvedLanguageIds);
		}
	}

	return query;
}

// Format value for Supabase filter string
function formatValue(value: any): string {
	if (Array.isArray(value))
		return `{${value.map(v => typeof v === 'string' ? `"${v}"` : v).join(',')}}`;
	if (typeof value === 'string')
		return value;
	if (value === null)
		return 'null';
	return String(value);
}

// Apply a single filter to query (for AND conditions only)
function applyFilter(query: any, filter: FilterResult): any {
	const { column, filter: filterType, value, clientSide } = filter;

	// Skip client-side filters in database query
	if (clientSide)
		return query;

	switch (filterType) {
		case 'eq': return query.eq(column, value);
		case 'neq': return query.neq(column, value);
		case 'ilike': return query.ilike(column, value);
		case 'not.ilike': return query.not(column, 'ilike', value);
		case 'is': return query.is(column, value);
		case 'not.is': return query.not(column, 'is', value);
		case 'ov': return query.overlaps(column, value);
		case 'cs': return query.contains(column, value);
		case 'in': return query.in(column, value);
		case 'not.in': return query.not(column, 'in', `{${value.join(',')}}`);
		case 'lt': return query.lt(column, value);
		case 'gt': return query.gt(column, value);
		case 'gte': return query.gte(column, value);
		case 'lte': return query.lte(column, value);
		default: return query;
	}
}

// Count matching results
export async function countResults(group: QueryGroup): Promise<number> {
	// Pre-resolve language conditions
	const languageConditions = collectLanguageConditions(group);
	const resolvedLanguageIds = new Map<string, string[]>();
	await Promise.all(
		languageConditions.map(async (cond) => {
			const ids = await resolveLanguageCondition(cond);
			resolvedLanguageIds.set(cond.id, ids);
		})
	);

	// For accurate count with client-side filters, we need to fetch all and count
	const clientSideFilters = collectClientSideFilters(group, resolvedLanguageIds);

	if (clientSideFilters.length > 0 || languageConditions.length > 0) {
		// Have to do full query and count client-side
		const results = await executeQuery(group);
		return results.length;
	}

	// No client-side filters, can use database count
	const organizationUuid = await getCurrentOrganization();

	// @ts-ignore - candidates table
	let query = supabase
		.from('candidates')
		.select('id', { count: 'estimated', head: true })
		.eq('organization_uuid', organizationUuid);

	query = applyGroupFilters(query, group, false, resolvedLanguageIds);

	const { count, error } = await query;

	if (error) throw error;
	return count || 0;
}

// Serialize query to URL-safe string
export function serializeQueryToURL(group: QueryGroup): string {
	try {
		const json = JSON.stringify(group);
		return btoa(json);
	} catch (error) {
		console.error('Failed to serialize query:', error);
		return '';
	}
}

// Deserialize query from URL string
export function deserializeQueryFromURL(encoded: string): QueryGroup | null {
	try {
		const json = atob(encoded);
		const parsed = JSON.parse(json);
		// Basic validation
		if (parsed && typeof parsed === 'object' && 'id' in parsed && 'type' in parsed && 'conditions' in parsed)
			return parsed as QueryGroup;
		return null;
	} catch (error) {
		console.error('Failed to deserialize query:', error);
		return null;
	}
}

// Generate human-readable query preview
export function generateQueryPreview(group: QueryGroup): string {
	if (group.conditions.length === 0)
		return '';

	const parts: string[] = [];

	for (const item of group.conditions) {
		if (isQueryCondition(item)) {
			const field = getFieldDefinition(item.field);
			const fieldLabel = field?.label || item.field;
			const operatorLabel = getOperatorLabel(item.operator);
			const valueStr = formatPreviewValue(item.value);

			if (['is_empty', 'is_not_empty'].includes(item.operator))
				parts.push(`${fieldLabel} ${operatorLabel}`);
			else if (item.field === 'language') {
				if (item.operator === 'any_level')
					parts.push(`${fieldLabel} "${valueStr}" ${operatorLabel}`);
				else
					parts.push(`${fieldLabel} "${valueStr}" ${operatorLabel} ${CEFR_LEVEL_LABELS[item.secondValue as string] || item.secondValue}`);
			} else
				parts.push(`${fieldLabel} ${operatorLabel} "${valueStr}"`);
		} else if (isQueryGroup(item)) {
			const nested = generateQueryPreview(item);
			if (nested)
				parts.push(`(${nested})`);
		}
	}

	return parts.join(` ${group.type} `);
}

// Get operator label for preview
function getOperatorLabel(operator: string): string {
	const labels: Record<string, string> = {
		'equals': 'equals',
		'not_equals': 'does not equal',
		'contains': 'contains',
		'not_contains': 'does not contain',
		'starts_with': 'starts with',
		'ends_with': 'ends with',
		'is_empty': 'is empty',
		'is_not_empty': 'is not empty',
		'contains_any': 'contains any of',
		'contains_all': 'contains all of',
		'in': 'is one of',
		'not_in': 'is not one of',
		'before': 'is before',
		'after': 'is after',
		'between': 'is between',
		'any_level': 'at any level',
		'at_least': 'at least',
		'at_most': 'at most',
		'exactly': 'exactly'
	};
	return labels[operator] || operator;
}

// Format value for preview
function formatPreviewValue(value: string | string[] | number | boolean | null): string {
	if (value === null || value === '')
		return '';
	if (Array.isArray(value))
		return value.join(', ');
	return String(value);
}

// Export results to CSV
export function exportToCSV(candidates: Candidate[], filename?: string): void {
	const headers = ['Name', 'Email', 'Phone', 'Function', 'Location', 'Status', 'Skills', 'Created At'];
	const rows = candidates.map(c => [
		c.name || '',
		c.email || '',
		c.phone || '',
		c.function || '',
		c.location || '',
		c.status || '',
		(c.skills || []).join('; '),
		c.created_at ? new Date(c.created_at).toLocaleDateString() : ''
	]);

	const csvContent = [
		headers.join(','),
		...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
	].join('\n');

	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	const link = document.createElement('a');
	const url = URL.createObjectURL(blob);

	link.setAttribute('href', url);
	link.setAttribute('download', filename || `candidates-export-${new Date().toISOString().slice(0, 10)}.csv`);
	link.style.visibility = 'hidden';

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}
